// @flow
import React, { useState, useMemo, useCallback } from 'react';
import type { Plane as PlaneType } from '../../../../types';
import { TYPE } from '../../../../constants/targetConstants';
import { degToRad } from '../../../../utils';
import { createGeometry } from '../../../../utils/targetUtils';
import Feature from '../../../ui/openlayers/Feature';
import { getStyle, useTargetStyle } from '../style';
import Trail from '../Trail';
import DraggableLabel from '../DraggableLabel';
import { targetStyles, draggableLabelStyles, labelConnectorStyles } from './style';

export type Props = $ReadOnly<{|
    icao: string,
    planeBuffer: $ReadOnlyArray<PlaneType>,
    heading?: number,
    isActive: boolean,
    showLabel: boolean,
    showTrails: boolean,
    showDisconnected?: boolean,
    dispatch?: any,

    selectTarget: (target: null | $ReadOnly<{id: string, type: typeof TYPE.plane}>) => void
|}>;

function Plane({ icao, planeBuffer, heading, isActive, showLabel, showTrails, showDisconnected, selectTarget }: Props) {
    const [selected, setSelected] = useState(false);
    const plane = planeBuffer && planeBuffer[0];
    const geometry = useMemo(() => plane && createGeometry(plane, degToRad(heading || 0)), [plane, heading]);
    const label = plane && plane.TargetIdentification;
    const rotation = degToRad(heading || 0);
    const style = useTargetStyle(getStyle(targetStyles, selected, 'system'), { label, rotation });

    const properties = useMemo(() => ({
        type: 'target',
        targetType: TYPE.plane,
        icao: icao,
        label: label
    }), [icao, label]);

    const onSelect = useCallback(() => {
        setSelected(true);
        selectTarget({
            id: icao,
            type: TYPE.plane
        });
    }, [icao, selectTarget]);
    const onDeselect = useCallback(() => {
        setSelected(false);
        selectTarget(null);
    }, [selectTarget]);

    if(!plane) {
        console.warn(`Entity data not found for plane "${icao}"`);
        return null;
    }

    if(!isActive && !showDisconnected) {
        return null;
    }

    return (
        <React.Fragment>
            <Feature
                style={style}
                geometry={geometry}
                properties={properties}
                onSelect={onSelect}
                onDeselect={onDeselect}
            />

            {showLabel &&
                <DraggableLabel
                    targetType={TYPE.plane}
                    target={plane}
                    selected={selected}
                    dataSource="system"
                    labelStyles={draggableLabelStyles}
                    connectorStyles={labelConnectorStyles}
                    onSelect={onSelect}
                    onDeselect={onDeselect}
                />
            }
            {showTrails && planeBuffer && <Trail buffer={planeBuffer} />}
        </React.Fragment>
    );
}

export default Plane;